// React Dependencies
import React, { useEffect, useMemo, useRef, useState } from 'react';

// External Dependencies
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Close } from '@material-ui/icons';

// Internal Dependencies
import _ from 'lodash';
import MaterialTable from 'material-table';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Status } from '../../../../common/constants';
import MText from '../../../../components/Common/MText';
import { IsEmpty } from '../../../../common/checks';
import UpdatePromoCodeModal from './UpdatePromoCodeModal';
import DeleteModal from './DeleteModal';
import MButton from '../../../../components/MButton';
import Colours from '../../../../styles/colours';
import MaterialDatePicker from '../../../../components/Common/form/MaterialDatePicker';
// eslint-disable-next-line import/no-named-as-default
import CustomMTableHeader from './CustomMTableHeader';
import StatusFilter from './StatusFilter';
import promoCodeAction from '../../../../actions/promoCode';

const Mode = {
  Update: 'Update',
  Delete: 'Delete',
};

const BulkUpdateDeleteModal = (props) => {
  const { gifts, showLoadingOverlay } = props.promoCode;

  const tableRef = useRef(null);

  const [selectedRows, setSelectedRows] = useState(new Set());
  const [isAllRowsSelected, setIsAllRowsSelected] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(Status.Unredeemed);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [mode, setMode] = useState(Mode.Update);

  const columns = useMemo(() => [
    {
      title:    'Code',
      field:    'code',
      editable: 'never',
      sorting:  false,
    },
    {
      title:           'Gift ID',
      field:           'giftId',
      editable:        'never',
      sorting:         false,
      cellStyle:       { width: '25%' },
      filterComponent: (filterProps) => (
        <Select
          className="w-100"
          variant="standard"
          style={{ maxWidth: 200 }}
          onChange={(e) => filterProps.onFilterChanged(filterProps.columnDef.tableData.id, e.target.value)}
          customFilterAndSearch={(filterTerm, rowData) => {
            if (!filterTerm) { return true; }
            return rowData.giftId === filterTerm;
          }}
        >
          <MenuItem value="" style={{ fontSize: 14 }}>
            <em>None</em>
          </MenuItem>
          {
            !IsEmpty(gifts) && (
              _.map(gifts, (gift) => (
                <MenuItem key={gift.id} style={{ fontSize: 14 }} value={gift.id}>
                  {gift.label}
                  {' '}
                  -
                  {' '}
                  {gift.id}
                </MenuItem>
              ))
            )
          }
        </Select>
      ),
    },
    {
      title:                 'Status',
      field:                 'status',
      editable:              'never',
      sorting:               false,
      defaultFilter:         Status.Unredeemed,
      // eslint-disable-next-line react/jsx-props-no-spreading
      filterComponent:       (filterComponent) => <StatusFilter selectedStatus={selectedStatus} {...filterComponent} />,
      customFilterAndSearch: (filterTerm, rowData) => {
        if (!filterTerm) { return true; }
        return rowData.status === filterTerm;
      },
    },
    {
      title:           'Expiry Date',
      field:           'expiryDate',
      editable:        'never',
      sorting:         false,
      filterComponent: (filterProps) => (
        <MaterialDatePicker
          minDate={false}
          onChange={(date) => filterProps.onFilterChanged(filterProps.columnDef.tableData.id, date)}
          variant="outlined"
          clearable
        />
      ),
      customFilterAndSearch: (filterTerm, rowData) => {
        if (!filterTerm) { return true; }

        const rowDate = new Date(rowData.expiryDate);
        const filterDate = new Date(filterTerm);

        return (
            rowDate.getFullYear() === filterDate.getFullYear()
            && rowDate.getMonth() === filterDate.getMonth()
            && rowDate.getDate() === filterDate.getDate()
        );
      },
      render: (row) => moment(row.expiryDate).utcOffset('+08', false).format('DD MMM YYYY'),
    },
    {
      title:           'Created At',
      field:           'createdAt',
      editable:        'never',
      sorting:         false,
      filterComponent: (filterProps) => (
        <MaterialDatePicker
          minDate={false}
          onChange={(date) => filterProps.onFilterChanged(filterProps.columnDef.tableData.id, date)}
          variant="outlined"
          clearable
        />
      ),
      customFilterAndSearch: (filterTerm, rowData) => {
        if (!filterTerm) { return true; }

        const rowDate = new Date(rowData.createdAt);
        const filterDate = new Date(filterTerm);

        return (
            rowDate.getFullYear() === filterDate.getFullYear()
            && rowDate.getMonth() === filterDate.getMonth()
            && rowDate.getDate() === filterDate.getDate()
        );
      },
      render: (row) => moment(row.createdAt).utcOffset('+08', false).format('DD MMM YYYY, HH:mm:ss'),
    },
    {
      title:           'Last Updated At',
      field:           'updatedAt',
      editable:        'never',
      sorting:         false,
      filterComponent: (filterProps) => (
        <MaterialDatePicker
          minDate={false}
          onChange={(date) => filterProps.onFilterChanged(filterProps.columnDef.tableData.id, date)}
          variant="outlined"
          clearable
        />
      ),
      customFilterAndSearch: (filterTerm, rowData) => {
        if (!filterTerm) { return true; }

        const rowDate = new Date(rowData.updatedAt);
        const filterDate = new Date(filterTerm);

        return (
            rowDate.getFullYear() === filterDate.getFullYear()
            && rowDate.getMonth() === filterDate.getMonth()
            && rowDate.getDate() === filterDate.getDate()
        );
      },
      render: (row) => moment(row.updatedAt).utcOffset('+08', false).format('DD MMM YYYY, HH:mm:ss'),
    },
  ], [gifts, selectedStatus]);

  const actionCol = useMemo(() => [(row) => ({ icon: () => <Checkbox checked={selectedRows.has(row.id)} /> })], [selectedRows]);

  const checkAllUpdateableRowsSelected = () => {
    if (tableRef.current) {
      const filteredRows = tableRef.current.dataManager.filteredData;
      if (filteredRows.length === 0) return false;
      return filteredRows.every((row) => selectedRows.has(row.id));
    }
    return false;
  };

  const handleSelectAllChange = (e) => {
    if (e.target.checked) {
      const selected = tableRef.current.dataManager.filteredData;
      const newSelectedRows = new Set(selected.map((row) => row.id));
      setSelectedRows(newSelectedRows);
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleRowSelected = (rowId) => {
    if (selectedRows.has(rowId)) {
      const filtered = [...Array.from(selectedRows).filter((id) => id !== rowId)];
      setSelectedRows(new Set(filtered));
    } else {
      const newSelectedRows = new Set([...Array.from(selectedRows), rowId]);
      setSelectedRows(newSelectedRows);
    }
  };

  const handleModeChange = (e) => {
    props.dispatchShowLoadingOverlay();
    setMode(e.target.value);
    setSelectedRows(new Set());
  };

  const handleFilterChange = () => {
    setSelectedRows(new Set());
  };

  useEffect(() => {
    if (!props.open) {
      setSelectedRows(new Set());
    }
  }, [props.open]);

  useEffect(() => {
    if (props.open && tableRef.current) {
      if (mode === Mode.Update) {
        setSelectedStatus(Status.Unredeemed);
      } else if (mode === Mode.Delete) {
        setSelectedStatus(Status.Activated);
      }
    }
  }, [mode, props.open]);

  useEffect(() => {
    if (tableRef.current) {
      if (selectedStatus === Status.Unredeemed) {
        tableRef.current.onFilterChange(2, Status.Unredeemed); // 2 is the index of status column
      } else {
        tableRef.current.onFilterChange(2, Status.Activated);
      }
    }
  }, [selectedStatus]);

  useEffect(() => {
    const res = checkAllUpdateableRowsSelected();
    setIsAllRowsSelected(res);
  }, [selectedRows]);

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle style={{ paddingBottom: 0 }}>
        <div className="d-flex justify-content-between align-items-center">
          <MText variant="h6" bold size={16}>Bulk Update/Delete</MText>
          <IconButton aria-label="close" onClick={props.onClose}>
            <Close />
          </IconButton>
        </div>
        <FormControl component="fieldset">
          <FormLabel style={{ fontSize: 14 }}>Operation</FormLabel>
          <RadioGroup className="flex flex-row" aria-label="mode" value={mode} onChange={handleModeChange}>
            <FormControlLabel value={Mode.Update} control={<Radio />} label="Update" />
            <FormControlLabel value={Mode.Delete} control={<Radio />} label="Delete" className="ml-3" />
          </RadioGroup>
        </FormControl>
        <div className="mt-3" />
      </DialogTitle>

      <DialogContent>
        <MaterialTable
          title=""
          isLoading={showLoadingOverlay}
          tableRef={tableRef}
          columns={columns}
          data={props.fetchPromoCodes}
          components={{
            // eslint-disable-next-line react/jsx-props-no-spreading
            Header: (headerProps) => (<CustomMTableHeader {...headerProps} />),
            Action: (actionProps) => (
              <Checkbox
                checked={selectedRows.has(actionProps.data.id)}
                onChange={() => handleRowSelected(actionProps.data.id)}
              />
            ),
          }}
          onFilterChange={handleFilterChange}
          actions={actionCol}
          localization={{
            header: {
              actions: <Checkbox checked={isAllRowsSelected} onChange={handleSelectAllChange} />,
            },
          }}
          options={{
            filtering:           true,
            toolbar:             false,
            emptyRowsWhenPaging: false,
            pageSizeOptions:     [5, 10, 25, 50, 100],
            debounceInterval:    500,
            actionsColumnIndex:  0,
          }}
        />
        <DeleteModal
          open={isDeleteModalOpen}
          selectedPromoCodes={Array.from(selectedRows)}
          pageId={props.page.selected.id}
          onClose={() => setIsDeleteModalOpen(false)}
          onComplete={async () => {
            setSelectedRows(new Set());
            setIsDeleteModalOpen(false);
            props.onClose();
            props.onActionCompleted();
          }}
        />
        <UpdatePromoCodeModal
          mode="Multiple"
          isModalOpen={isUpdateModalOpen}
          pageId={props.page.selected.id}
          onClose={() => setIsUpdateModalOpen(false)}
          selectedPromoCodesId={Array.from(selectedRows)}
          onComplete={async () => {
            props.onClose();
            await props.onActionCompleted();
            toast.success('Promo codes updated successfully');
          }}
        />
      </DialogContent>

      <DialogActions
        style={{
          padding: '16px 24px',
        }}
      >
        <div className="w-100">
          <MText>
            Selected records:
            {' '}
            {selectedRows.size}
            {' '}
            {selectedRows.size > 1 ? 'records' : 'record'}
          </MText>

          <div className="d-flex flex-row justify-content-end mr-3">
            {
              mode === Mode.Update ? (
                <MButton
                  variant="contained"
                  colour={selectedRows.size === 0 ? Colours.LightGray2 : Colours.Secondary}
                  textColour={Colours.White}
                  disabled={selectedRows.size === 0}
                  onClick={() => {
                    setIsUpdateModalOpen(true);
                  }}
                  text="Update"
                />
              ) : (
                mode === Mode.Delete ? (
                  <MButton
                    variant="contained"
                    colour={selectedRows.size === 0 ? Colours.LightGray2 : Colours.Secondary}
                    textColour={Colours.White}
                    disabled={selectedRows.size === 0}
                    onClick={() => {
                      setIsDeleteModalOpen(true);
                    }}
                    text="Delete"
                  />
                ) : null
              )
            }
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

BulkUpdateDeleteModal.propTypes = {
  open:                       PropTypes.bool.isRequired,
  onClose:                    PropTypes.func.isRequired,
  onActionCompleted:          PropTypes.func.isRequired,
  fetchPromoCodes:            PropTypes.func.isRequired,
  dispatchShowLoadingOverlay: PropTypes.func.isRequired,
  page:                       PropTypes.shape({
    selected: PropTypes.shape({
      id: PropTypes.number,
    }),
  }).isRequired,
  promoCode: PropTypes.shape({
    promoCodes:         PropTypes.arrayOf(PropTypes.shape({})),
    gifts:              PropTypes.arrayOf(PropTypes.shape({})),
    showLoadingOverlay: PropTypes.bool,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  promoCode: state.promoCode,
  page:      state.page,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchShowLoadingOverlay: () => promoCodeAction.showPromoCodeLoadingOverlay(dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkUpdateDeleteModal);
